import { Badge, Card, CardContent, cn, PageHeader, Spinner } from '@estac1/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { ConversationDetail } from './assistant/conversation-detail.js';
import { ReviewList } from './assistant/review-list.js';
import { TopicList } from './assistant/topic-list.js';

interface ConversationRow {
  id: string;
  lotName: string | null;
  ownerName: string | null;
  updatedAt: string;
  questions: number;
  blocked: number;
  firstQuestion: string | null;
}

const when = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

/**
 * Revisão das conversas do assistente de suporte (#614): as últimas 50, com o
 * que foi barrado em destaque. Serve para ajustar o conhecimento e achar abuso.
 */
export const AssistantScreen = () => {
  const [openId, setOpenId] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['admin-assistant-conversations'],
    queryFn: () =>
      api.request<{ conversations: ConversationRow[] }>('/v1/admin/assistant/conversations'),
  });

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <PageHeader
        title="Assistente — conversas"
        description="O que os donos perguntam ao suporte do painel e o que foi respondido. Liga, desliga e limites ficam em Preços."
      />
      <TopicList />
      <ReviewList onOpen={setOpenId} />
      {list.isLoading ? (
        <Spinner />
      ) : !list.data || list.data.conversations.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-fg">
            Nenhuma conversa ainda.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
          <ul className="divide-y divide-border rounded border border-border bg-surface">
            {list.data.conversations.map((conv) => (
              <li key={conv.id}>
                <button
                  type="button"
                  onClick={() => setOpenId(conv.id)}
                  className={cn(
                    'w-full space-y-1 px-3 py-2 text-left text-sm hover:bg-muted',
                    openId === conv.id && 'bg-muted',
                  )}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate font-medium">{conv.lotName ?? 'Lote removido'}</span>
                    <span className="shrink-0 text-xs text-muted-fg">{when(conv.updatedAt)}</span>
                  </span>
                  <span className="block truncate text-muted-fg">{conv.firstQuestion}</span>
                  <span className="flex gap-2 text-xs text-muted-fg">
                    {conv.ownerName} · {conv.questions} perguntas
                    {conv.blocked > 0 && <Badge variant="danger">{conv.blocked} barradas</Badge>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {openId ? (
            <ConversationDetail id={openId} />
          ) : (
            <p className="text-sm text-muted-fg">Escolha uma conversa para ler.</p>
          )}
        </div>
      )}
    </div>
  );
};
